import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, Archive, CheckCircle2, FileCheck2, RotateCcw } from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppText } from '../components/ui/AppText';
import { tokens } from '../theme/tokens';

const resultMetrics = [
  { label: '本次得分', value: '82', helper: '满分 100' },
  { label: '错题', value: '6', helper: '3 道可直接再练' },
  { label: '可提升', value: '12', helper: '来自薄弱知识点' },
];

const correctionItems = [
  { title: '选择题第 8 题', status: '计算过程漏项', tone: 'warning' as const },
  { title: '填空题第 12 题', status: '二次函数顶点式不熟', tone: 'danger' as const },
  { title: '解答题第 21 题', status: '步骤完整度不足', tone: 'warning' as const },
];

export default function ExamResultPage() {
  const router = useRouter();

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="上传"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
        <View style={styles.headerCopy}>
          <AppText variant="title" weight="bold">
            批改结果
          </AppText>
          <AppText variant="caption" tone="muted">
            ExamResultScreen demo 结果和归档入口
          </AppText>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <AppCard style={styles.successCard}>
          <CheckCircle2 size={28} color={tokens.colors.success} />
          <View style={styles.successCopy}>
            <AppText variant="subtitle" weight="bold">
              批改完成
            </AppText>
            <AppText variant="body" tone="muted">
              已生成本地批改结果，可查看错题分布或归档为一次考试报告。
            </AppText>
          </View>
        </AppCard>

        <View style={styles.metricsGrid}>
          {resultMetrics.map((metric) => (
            <AppCard key={metric.label} style={styles.metricCard}>
              <AppText variant="metric" tone="primary" weight="bold">
                {metric.value}
              </AppText>
              <AppText variant="caption" weight="bold">
                {metric.label}
              </AppText>
              <AppText variant="caption" tone="muted">
                {metric.helper}
              </AppText>
            </AppCard>
          ))}
        </View>

        <AppCard style={styles.sectionCard}>
          <View style={styles.sectionHeader}>
            <FileCheck2 size={20} color={tokens.colors.primary} />
            <AppText variant="subtitle" weight="bold">
              错题批改摘要
            </AppText>
          </View>
          {correctionItems.map((item) => (
            <View key={item.title} style={styles.issueRow}>
              <View style={styles.issueCopy}>
                <AppText variant="body" weight="bold">
                  {item.title}
                </AppText>
                <AppText variant="caption" tone="muted">
                  {item.status}
                </AppText>
              </View>
              <AppText variant="caption" tone={item.tone} weight="bold">
                待巩固
              </AppText>
            </View>
          ))}
        </AppCard>

        <View style={styles.actions}>
          <AppButton
            label="归档到考试报告"
            onPress={() => router.push('/exam-detail')}
            leftSlot={<Archive size={18} color={tokens.colors.white} />}
          />
          <AppButton
            label="重新上传"
            variant="secondary"
            onPress={() => router.push('/exam-upload')}
            leftSlot={<RotateCcw size={18} color={tokens.colors.primary} />}
          />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  successCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    backgroundColor: tokens.colors.successSoft,
  },
  successCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  metricsGrid: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  metricCard: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  sectionCard: {
    gap: tokens.spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  issueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
    backgroundColor: tokens.colors.background,
  },
  issueCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
  actions: {
    gap: tokens.spacing.md,
  },
});
